import { useEffect, useState } from 'react';

import AddImage from '../../../../reusable/images/add-image/add-image.component';
import Spinner from '../../../../reusable/spinner/spinner.component';
import Client from '../../../../../tools/client';

import {
  FieldSection,
  FieldGroup,
  Label,
  Description,
  Input,
  Toggle,
  ButtonRow,
  Button
} from '../../configuration-sections.styles';

const client = new Client();

const BackgroundConfigurationDisplay = () => {
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(false);
  const [form, setForm] = useState({
    backgroundImageActive: false,
    backgroundImagePosition: '',
    backgroundImageSize: '',
    backgroundImageRepeat: '',
    backgroundOverlayActive: false,
    backgroundOverlayColor: ''
  });

  useEffect(() => {
    const fetchData = async () => {
      const res = await client.setup();
      const app = res.configuration?.app || {};

      setForm({
        backgroundImageActive:
          app.background?.image?.active === 'true' || app.background?.image?.active === true,
        backgroundImagePosition: app.background?.image?.styling?.position || '',
        backgroundImageSize: app.background?.image?.styling?.size || '',
        backgroundImageRepeat: app.background?.image?.styling?.repeat || '',
        backgroundOverlayActive:
          app.background?.image?.overlay?.active === 'true' || app.background?.image?.overlay?.active === true,
        backgroundOverlayColor: app.background?.image?.styling?.overlay?.color || ''
      });

      setLoading(false);
    };

    fetchData();
  }, []);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm({ ...form, [name]: value });
  };

  const handleToggle = (key) => {
    setForm({ ...form, [key]: !form[key] });
  };

  const handleSave = async () => {
    const updates = {
      'background.image.active': String(form.backgroundImageActive),
      'background.image.styling.position': form.backgroundImagePosition,
      'background.image.styling.size': form.backgroundImageSize,
      'background.image.styling.repeat': form.backgroundImageRepeat,
      'background.image.overlay.active': String(form.backgroundOverlayActive),
      'background.image.styling.overlay.color': form.backgroundOverlayColor
    };

    for (const [key, value] of Object.entries(updates)) {
      await client.updateConfigurationByKey(`app.${key}`, { value });
    }

    setEditing(false);
  };

  if (loading) return <Spinner />;

  return (
    <FieldSection>
      <FieldGroup>
        <Label>Background Image</Label>
        <Description>Select a background image to be shown on the site. This image will appear behind main content.</Description>
        <AddImage
          configKey="app.background.image"
          scope="app"
          joinTable="ConfigurationEntryImages"
          disabled={!editing}
        />
      </FieldGroup>

      <FieldGroup>
        <Label>Background Image Active</Label>
        <Description>Toggle whether the background image should be visible.</Description>
        <Toggle>
          <input
            type="checkbox"
            checked={form.backgroundImageActive}
            onChange={() => handleToggle('backgroundImageActive')}
            disabled={!editing}
          />
          <span />
        </Toggle>
      </FieldGroup>

      <FieldGroup>
        <Label htmlFor="backgroundImagePosition">Background Position</Label>
        <Description>Controls the image alignment (e.g., center, top, bottom, left, right).</Description>
        <Input
          name="backgroundImagePosition"
          value={form.backgroundImagePosition}
          onChange={handleChange}
          disabled={!editing}
        />
      </FieldGroup>

      <FieldGroup>
        <Label htmlFor="backgroundImageSize">Background Size</Label>
        <Description>Defines how the image scales (e.g., cover, contain, auto).</Description>
        <Input
          name="backgroundImageSize"
          value={form.backgroundImageSize}
          onChange={handleChange}
          disabled={!editing}
        />
      </FieldGroup>

      <FieldGroup>
        <Label htmlFor="backgroundImageRepeat">Background Repeat</Label>
        <Description>Set whether the image repeats (e.g., no-repeat, repeat-x, repeat-y, repeat).</Description>
        <Input
          name="backgroundImageRepeat"
          value={form.backgroundImageRepeat}
          onChange={handleChange}
          disabled={!editing}
        />
      </FieldGroup>

      <FieldGroup>
        <Label>Overlay Active</Label>
        <Description>Enable a semi-transparent overlay above the background image for contrast or aesthetic effect.</Description>
        <Toggle>
          <input
            type="checkbox"
            checked={form.backgroundOverlayActive}
            onChange={() => handleToggle('backgroundOverlayActive')}
            disabled={!editing}
          />
          <span />
        </Toggle>
      </FieldGroup>

      <FieldGroup>
        <Label htmlFor="backgroundOverlayColor">Overlay Color</Label>
        <Description>Set the overlay color (e.g., rgba(0,0,0,0.5) for dark overlay).</Description>
        <Input
          name="backgroundOverlayColor"
          value={form.backgroundOverlayColor}
          onChange={handleChange}
          disabled={!editing}
        />
      </FieldGroup>

      <ButtonRow>
        {editing ? (
          <>
            <Button onClick={() => setEditing(false)}>Cancel</Button>
            <Button primary onClick={handleSave}>Save</Button>
          </>
        ) : (
          <Button primary onClick={() => setEditing(true)}>Edit</Button>
        )}
      </ButtonRow>
    </FieldSection>
  );
};

export default BackgroundConfigurationDisplay;